import React, { useState, useRef, useEffect, useCallback } from "react";

import {
	Button,
	Divider,
	Dropdown,
	Form,
	Icon,
	Input,
	Popup,
	Radio,
} from "semantic-ui-react";
import BrandSkuPicker from "../../common/BrandSkuPicker";
import { useIntl } from "react-intl";

import {useForm} from "../../../libs/component_utils";
import "react-datepicker/dist/react-datepicker.css";
import PixelsService from "../../../services/pixels";
import T1Service from "../../../services/t1";
import AgenciesService from "../../../services/agencies";
import PropTypes from "prop-types";
import {Config} from "../../../config/api";
import HttpConnect from "../../../libs/http_connect"
import { getReadableClientName } from "../../../libs/common_utils";
import { default1PSeller } from "../../../libs/consts";
import PermissionsService from "../../../services/permissions";
import { walmartMXTranslations } from "../../../translations/walmartMXTranslations";

const clientName = getReadableClientName(Config.public_client)

const should_do_search = (query, prev_query) => {
	return prev_query !== query && query.length >= 1;
};

const checkIfAdminAgency = (agencyPermissions) => {
	return agencyPermissions.length === 1 && agencyPermissions[0].advertisers.length === 0 && agencyPermissions[0].seller_ids.length === 0 && agencyPermissions[0].brands.length === 0;
}

/**
 * Roi Pixel create page component
 * @return {*}
 * @constructor
 */
const PixelRoiForm = ({
	formSending,
	initialData,
	onSubmit,
	onCancel,
	submitText,
	formType = "create"
}) => {
	const services = useRef(
		new Map([
			["pixels", new PixelsService()],
			["t1", new T1Service()],
			["agencies", new AgenciesService()],
			["permissions", new PermissionsService()]
		])
	);

  const sellerQuery = useRef("");
  const sellerTimer = useRef(0);

	const intl = useIntl();
	const editMode = formType.toString() === "edit";
	const agency = services.current.get("agencies").getSelectedAgency() || 0;
	const [agencyPermissions, setAgencyPermissions] = useState([]);
	const [filteredPermissions, setFilteredPermissions] = useState([]);
	const [enabledAdvertiserIds, setEnabledAdvertiserIds] = useState([]);
	const [hasFetchedPermissions, setHasFetchedPermissions] = useState(false);
	const [selectedSkus, setSelectedSkus] = useState(() => {
		if (editMode && initialData.sku_ids) {
			const ids = Array.isArray(initialData.sku_ids)
				? initialData.sku_ids
				: initialData.sku_ids.split(",").map(id => id.trim()).filter(id => id.length > 0);
			return ids.map(sku_id => ({
				sku_id,
				item_name: (initialData.item_names && initialData.item_names[sku_id]) || null,
			}));
		}
		return [];
	});
	const get1pAdvertiserIds = useCallback((permissions) => {
		return permissions
			.flatMap(permission => permission.advertisers.map(advertiser => advertiser.id));
	}, []);

  const [fetchedSellers, setFetchedSellers] = useState([]);
	// loaded page data
  const [pageData, setPageData] = useState(() => {
    let initialSellers = [
      {
        key: 'hint',
        text: intl.formatMessage({
          id: "PLACEHOLDER_START_TYPING_3P",
          defaultMessage: "Start typing to search for 3P sellers...",
        }),
        value: 'hint',
        disabled: true,
      }
    ];

    // If in edit mode and we have seller data, add the current seller
    if (editMode && initialData.seller_id) {
      // Skip if it's the 1P seller
      if (initialData.seller_id !== '0') {
        initialSellers.push({
          key: initialData.seller_id,
          text: initialData.seller_id,
          value: initialData.seller_id
        });
      }
    }

    return {
      advertisersLoading: true,
      advertisers: [],
      sellers: initialSellers,
      sellersLoading: false
    };
  });

	/**
	 * initially loading advertisers list
	 */
  useEffect(() => {
    const loadAdvertisers = async () => {
      try {
        const res = await services.current.get("t1").advertisers(agency);
        setPageData(prev => ({
          ...prev,
          advertisersLoading: false,
          advertisers: res.data.map(({ id, title }) => ({ key: id, text: title, value: id }))
        }));
      } catch (e) {
        console.error(e);
      }
    };
    loadAdvertisers();
  }, []);

	const fetchAgencyPermissions = useCallback(async () => {
		try {
			const response = await services.current.get("permissions").getAllPermissionsForROIPixelPage({agency_id: agency});
			const currentAgencyPermissions = response.data.filter(permission => permission.agency_id === agency);
			const filteredPermissions = currentAgencyPermissions.filter(permission => permission.seller_ids[0] === default1PSeller.value);
			setAgencyPermissions(currentAgencyPermissions);
			setFilteredPermissions(filteredPermissions);
			setEnabledAdvertiserIds(get1pAdvertiserIds(filteredPermissions));
		} catch (error) {
			console.error("Error fetching permissions:", error);
		} finally {
			setHasFetchedPermissions(true);
    }
	}, []);

	useEffect(() => {
		fetchAgencyPermissions();
	}, [])

	/**
	 * form submit handler
	 * @return {boolean}
	 */
	const addPixelForm = async () => {
		try {
			await onSubmit({
				...values,
				sku_ids: selectedSkus.map(s => s.sku_id).join(","),
			});
		} catch (e) {
			console.error(e);
		}
	};

	const {values, errors, onChange, updateValues, onSubmit: handleSubmit} = useForm(
		addPixelForm,
		initialData,
		() => {
			const errors = {};

			if (values.advertiser_id === null) {
				errors.advertiser_id = intl.formatMessage({
					id: "ERROR_EMPTY_ADVERTISER_ID",
					defaultMessage: "Please select an advertiser.",
				});
			}

      if (values.seller_id === null) {
				errors.seller_id = intl.formatMessage({
					id: "VALIDATOR_SELECT_SELLER",
					defaultMessage: "Please select a seller",
				});
			}

			if (values.brand_names.length < 1) {
				errors.brand_names = intl.formatMessage({
					id: "VALIDATOR_SELECT_BRAND",
					defaultMessage: "Please select at least one Brand",
				});
			}

			if(values.attribution_level === "sku" && selectedSkus.length < 1) {
				errors.sku_ids = intl.formatMessage({
					id: "VALIDATOR_SKU_NUMBERS",
					defaultMessage: "Please select at least one SKU.",
				});
			}

			return errors;
		}
	);

	const isAdminAgency = hasFetchedPermissions && checkIfAdminAgency(agencyPermissions);

	// Advertiser is 1P enabled if:
	// 1. The advertiser is in the list of enabled advertisers from permissions
	// 2. Or if there is only one permission and it has no advertisers (which means the 1P rule applies to all advertisers)
	const is1PEnabledAdvertiser = (hasFetchedPermissions && enabledAdvertiserIds.includes(values.advertiser_id?.toString())) || (filteredPermissions.length === 1 && filteredPermissions[0].advertisers.length === 0) || isAdminAgency;

	useEffect(() => {
		if (is1PEnabledAdvertiser) {
			setPageData(prev => ({
				...prev,
				sellers: [...prev.sellers.filter(seller => seller.value !== default1PSeller.value), default1PSeller]
			}))
		} else {
			setPageData(prev => ({
				...prev,
				sellers: prev.sellers.filter(seller => seller.value !== default1PSeller.value)
			}))
		}
	}, [is1PEnabledAdvertiser]);

  const handleSellersSearch = (_e, data) => {
    const prevSellerQuery = sellerQuery.current || "";
    sellerQuery.current = data.searchQuery;
    if(sellerTimer.current) clearTimeout(sellerTimer.current);
    if (!should_do_search(sellerQuery.current, prevSellerQuery)) {
			setPageData(prev => ({ ...prev, sellers: is1PEnabledAdvertiser ? [default1PSeller] : [], sellersLoading: false }));
			return;
		}
    setPageData(prev => ({ ...prev, sellers: [], sellersLoading: true }));
    HttpConnect.cancelRequest();

    sellerTimer.current = setTimeout(async () => {
      try {
        const sellers = await services.current.get("pixels").getSellers(sellerQuery.current, { apply_permissions: true, advertiser_id: values.advertiser_id, agency_id: agency });
        setFetchedSellers(sellers.data);
        if (sellers.data) {
          const formattedSellers = [
            ...(is1PEnabledAdvertiser ? [default1PSeller] : []),
            ...sellers.data.map(s => {
              return {
                key: s.seller_id,
                text: s.seller_id,
                value: s.seller_id
              };
            })
          ];
          setPageData(prev => ({...prev, sellers: formattedSellers, sellersLoading: false }));
        } else {
          setPageData(prev => ({
            ...prev,
            sellers: is1PEnabledAdvertiser ? [default1PSeller] : [],
            sellersLoading: false
          }));
        }
      } catch (e) {
        console.error("Error fetching sellers:", e);
        setPageData(prev => ({...prev, sellers: [], sellersLoading: false }));
      }
    }, Config.search_debounce_delay);
  };

	const handleAdvertiserChange = (_e, data) => {
		const { value } = data;
		if (value !== values.advertiser_id) {
			updateValues({
				...values,
				advertiser_id: value,
				"brand_names": [],
				"sku_ids": "",
				"seller_id": null,
				"seller_name": null
			});
			setSelectedSkus([]);
		}
	}

  const attribution_hint = !values.seller_id
  ? intl.formatMessage({
      id: "HINT_ATTRIBUTION_LOGIC_NO_SELLER",
      defaultMessage: "Please select a seller first to configure the attribution logic for this ROI Pixel."
    })
  : values.attribution_level === "sku"
    ? intl.formatMessage({
        id: "HINT_ATTRIBUTION_LOGIC_WITH_SKU",
        defaultMessage: "Please enter a (list of) brand(s) and SKU's to attribute conversion and revenue to, for this ROI Pixel. Purchase events that contain the selected brand(s) and SKU's will be used for ROI calculation"
      })
    : intl.formatMessage({
        id: "HINT_ATTRIBUTION_LOGIC",
        defaultMessage: "Please enter a (list of) brand(s) to attribute conversion and revenue to, for this ROI Pixel. Purchase events that contain the selected brand(s) will be used for ROI calculation"
      });

	return (
		<Form
			onSubmit={handleSubmit}
			size="small"
			loading={formSending}
			noValidate
			error={!!Object.keys(errors).length}
			autoComplete="off"
		>
			<Form.Field inline>
				<label>
					{intl.formatMessage({
						id: "LABEL_STATUS",
						defaultMessage: "Status",
					})}
				</label>
				<Radio
					data-testid="roi-pixel-status-radio-active"
					name="status"
					label={intl.formatMessage({
						id: "STATUS_ACTIVE",
						defaultMessage: "Active",
					})}
					value={1}
					checked={!!values.status}
					onChange={onChange}
				/>
				<Radio
					data-testid="roi-pixel-status-radio-inactive"
					style={{ "marginLeft": "15px" }}
					name="status"
					label={intl.formatMessage({
						id: "STATUS_INACTIVE",
						defaultMessage: "Inactive",
					})}
					value={0}
					checked={!values.status}
					onChange={onChange}
				/>
			</Form.Field>

			<Form.Field inline error={errors.hasOwnProperty("title")} required>
				<label>
					{intl.formatMessage({
						id: "LABEL_PIXEL_NAME",
						defaultMessage: "Pixel Name",
					})}
				</label>
				<div className="custom-error-container">
					<Input
						aria-label="Pixel Name"
						name="title"
						required
						minLength={1}
						maxLength={256}
						defaultValue={values.title}
						onBlur={onChange}
					/>
					<div className="custom-error">{errors["title"]}</div>
				</div>
			</Form.Field>

			<Form.Field
				inline
				error={errors.hasOwnProperty("advertiser_id")}
				required
			>
				<label>
					{intl.formatMessage({
						id: "LABEL_ADVERTISER",
						defaultMessage: "Advertiser",
					})}
				</label>
				<div className="custom-error-container" style={{"width": "25%"}}>
					<Dropdown
						aria-label="Advertiser"
						required
						search
						selection
						disabled={editMode}
						options={pageData.advertisers}
						loading={pageData.advertisersLoading}
						placeholder={intl.formatMessage({
							id: "HINT_ADVERTISER",
							defaultMessage: "Select advertiser",
						})}
						noResultsMessage={intl.formatMessage({
							id: "EMPTY_SEARCH_RESULTS",
							defaultMessage: "No results found",
						})}
						name="advertiser_id"
						value={values.advertiser_id}
						onChange={handleAdvertiserChange}
            style={{"width": "100%"}}
					/>
					<div className="custom-error">{errors["advertiser_id"]}</div>
				</div>
			</Form.Field>

      <Form.Field
				disabled={!values.advertiser_id}
        inline
        error={errors.hasOwnProperty("seller_id")}
        required>
        <label>
          {intl.formatMessage({
            id: "LABEL_SELLER",
            defaultMessage: "Seller",
          })}
        </label>
        <div className="custom-error-container" style={{"width": "25%"}}>
          <Dropdown
						aria-label="Seller"
						disabled={!values.advertiser_id}
            required
            search
            selection
            options={pageData.sellers}
            loading={pageData.sellersLoading}
            placeholder={intl.formatMessage({
              id: "PLACEHOLDER_SELECT_SELLER",
              defaultMessage: "Select Seller",
            })}
            noResultsMessage={pageData.sellersLoading? "" : sellerQuery.current.length? intl.formatMessage({
              id: "PLACEHOLDER_NOT_FOUND",
              defaultMessage: "Not found",
            }) : intl.formatMessage({
              id: "PLACEHOLDER_START_TYPING_3P",
              defaultMessage: "Start typing to search for 3P sellers...",
            })}
            name="seller_id"
            value={values.seller_id}
            onSearchChange={handleSellersSearch}
            onChange={(_e, { value }) => {
              const seller3P = fetchedSellers.find(seller => seller.seller_id === value);
              const sellerName = seller3P ? seller3P.seller_name : clientName;

              updateValues({
                ...values,
                "seller_id": value,
                "seller_name": sellerName,
                "brand_names": [],
                "sku_ids": ""
              });
              setSelectedSkus([]);
            }}
            style={{"width": "100%"}}
          />
          {errors["seller_id"] && (
            <div className="custom-error">{errors["seller_id"]}</div>
          )}
        </div>
      </Form.Field>

			<Form.Field inline>
				<label style={{"minWidth": "180px"}}>
					{intl.formatMessage({
						id: "LABEL_ATTRIBUTION_LOGIC",
						defaultMessage: "Attribution Logic"
					})}
					<Popup
            inverted
            style={{ marginLeft: "0.5em" }}
            content={intl.formatMessage({
							id: "TOOLTIP_ROI_PIXEL_PERMISSIONS",
							defaultMessage: "Please contact your Walmart account manager for brand and seller permissions"
						})}
            size="mini"
            trigger={
              <Icon
                name="help circle"
                style={{ position: "relative" }}
                className="cursor-help"
              />
            }
          />
				</label>
        <div style={{ display: "inline-block", verticalAlign: "top" }}>{attribution_hint}</div>
			</Form.Field>
			<BrandSkuPicker
				key={`${values.advertiser_id}-${values.seller_id}`}
				attribution_level={values.attribution_level}
				onAttributionLevelChange={onChange}
				brand_names={values.brand_names}
				onBrandNamesChange={(newBrands) => updateValues({ ...values, brand_names: newBrands })}
				initialBrandOptions={initialData.brand_names?.map(brand => ({ text: brand, value: brand })) || []}
				advertiser_id={values.advertiser_id}
				seller_id={values.seller_id}
				agency={agency}
				disabled={values.seller_id === null}
				errors={{ brand_names: errors.brand_names, sku_ids: errors.sku_ids }}
				selectedSkus={selectedSkus}
				setSelectedSkus={setSelectedSkus}
				helpText="Selecciona las marcas y UPC que deseas ver desglosadas en el reporte de Retail Attribution. Solo los elementos seleccionados se mostrarán con detalle en el reporte."
			/>
			<Divider hidden />
			<Divider hidden />
			<Divider hidden />
			<Form.Field align="right">
				<Button size="tiny" type="button" onClick={onCancel}>
					{intl.formatMessage({
						id: "BTN_CANCEL",
						defaultMessage: "Cancel",
					})}
				</Button>
        <Button size="tiny" color="green" type="submit" data-testid="roi-pixel-submit">
					{submitText}
				</Button>
			</Form.Field>
		</Form>
	);
};
PixelRoiForm.propTypes = {
	"formSending": PropTypes.bool.isRequired,
	"submitText": PropTypes.string.isRequired,
	"formType": PropTypes.string
};

export default PixelRoiForm;
